import type {
  Confirm,
  ConfirmResolveResponse,
  ConfirmTone,
} from "@/definitions/types";
import { defineStore } from "pinia";
import { ref } from "vue";

/**
 * Promise 기반 확인 다이얼로그 스토어
 * - 표시는 `ConfirmDialog.vue` 가 담당한다(Vuetify 미사용, 표준 모달 `AppModal`).
 * - `await confirmDelete()` 형태로 호출하고 boolean 결과만 받으면 된다.
 */
export const useConfirmStore = defineStore("confirm", () => {
  const items = ref<Confirm[]>([]);
  const formSaved = ref(true);

  async function confirmCreate(): Promise<boolean> {
    return pushConfirm("작성한 내용을 저장하시겠습니까?", "저장", "저장");
  }

  async function confirmUpdate(): Promise<boolean> {
    return pushConfirm("수정하시겠습니까?", "편집", "수정");
  }

  async function confirmDelete(): Promise<boolean> {
    return pushConfirm("삭제하시겠습니까?", "삭제", "삭제", "취소", {
      tone: "danger",
    });
  }

  /**
   * 확인창을 띄우고 사용자의 선택(확인 true · 취소 false)을 돌려준다.
   * - `options.tone: "danger"` 면 확인 버튼이 빨간 위험 버튼(`ui-danger`)이 된다 — 되돌릴 수 없는 동작(삭제 · 제거 · 폐기 · 초기화)에 쓴다.
   *   주지 않으면 지금처럼 primary 다(하위 호환).
   * - `options.confirmLabel` · `cancelLabel` 은 버튼 글자(72차 UXJ-03). 있으면 위치 인자(`confirmButtonTitle` · `cancelButtonTitle`)보다
   *   우선한다. 확인 버튼은 무엇을 하는지 동사로 쓴다(「이동」 · 「삭제」 · 「확정 해제」 · 「제거」) — 「확인」 은 기본값(하위 호환)일 뿐이다.
   */
  async function pushConfirm(
    content: string,
    title = "확인",
    confirmButtonTitle = "확인",
    cancelButtonTitle = "취소",
    options: {
      tone?: ConfirmTone;
      confirmLabel?: string;
      cancelLabel?: string;
      /** 본문 아래 「영향 자세히」 접힘에 보일 줄들(90차 C90-26 — 보관처럼 영향이 긴 확인창) */
      impactDetails?: string[];
    } = {},
  ): Promise<boolean> {
    return new Promise<ConfirmResolveResponse>((resolve) => {
      items.value = [
        ...items.value,
        {
          uuid: crypto.randomUUID(),
          title,
          content,
          cancelButtonTitle: options.cancelLabel ?? cancelButtonTitle,
          confirmButtonTitle: options.confirmLabel ?? confirmButtonTitle,
          tone: options.tone,
          impactDetails: options.impactDetails,
          resolver: resolve,
        },
      ];
    }).then((response) => {
      items.value = items.value.filter((item) => response.uuid !== item.uuid);
      return response.value;
    });
  }

  return {
    items,
    formSaved,
    confirmCreate,
    confirmUpdate,
    confirmDelete,
    pushConfirm,
  };
});
